// articles/StablefordGame.js
// Game guide (Oct 5, 2026): Stableford in TTO, start to finish, for a weekly round first.
// Oct 7, 2026: individual play only, like Dots — "Who plays whom": Within each group (each
// pairing group is its own Stableford) or Whole field. Team Stableford is retired. The pot
// can pay the whole round or front, back and overall ("What it pays").
// Written from the live code: the Stableford card in Games Setup (App.js
// StablefordGamePanel — Who plays whom, Scoring, Allowance, Money, places, What it pays), the engine
// (gameEngines/stableford.js), the PTS box on the hole screen (StablefordPtsCell), the
// Results tab and the settlement statement. Button names are quoted exactly as they
// appear, so if a label changes in App.js, change it here too.
//
// Linked from inside the app: 📖 Guides → Game guides. Public.

import React from 'react';
import { colors, getGuideBySlug } from '../guidesData';
import { H2, H3, P, UL, LI, GuideCTA, FAQ } from '../articleUI';
import { Step, Btn, Heads, StepTracker, PRINT_CSS } from './eventGuideParts';

const guide = getGuideBySlug('how-to-set-up-a-stableford-golf-game');

const FAQS = [
  {
    q: 'Why does a double bogey score the same as a quadruple?',
    a: 'That is the point of Stableford: a blow-up hole costs you at most the points for that hole. Once a hole is worth 0 you can pick up, tap X, and move on. It keeps the round moving and keeps everyone in the game.',
  },
  {
    q: 'Why 95%?',
    a: 'The USGA recommends a 95% handicap allowance for individual Stableford, the same as individual stroke play. TTO applies it to each player’s course handicap and rounds once. You can change it on the Stableford card.',
  },
  {
    q: 'Two players tie for first. Who wins the pot?',
    a: 'They share the money for the places they cover: two players tied for first in winner-take-all split the pot. The order on the results is still decided on the cards (the points over the last 9, 6, 3 and 1 holes), so the board reads cleanly.',
  },
  {
    q: 'Which money version should we use?',
    a: 'A pot is the classic: everyone buys in and the best totals collect. Per point vs the group (or the field) pays everyone by how far he is above or below the average. Per point, player by player settles every pair, like Putts; on the whole field it adds up quickly.',
  },
  {
    q: 'Within each group or the whole field?',
    a: 'Within each group, each foursome, threesome or twosome on the Pairings page plays its own Stableford: you compete with the players you can see, and a group of 3 next to a group of 4 is no problem, because nobody is measured against a group of a different size. The whole field puts everyone in one Stableford, which suits a bigger day where everyone wants one winner.',
  },
  {
    q: 'A player cancels at the last minute. What happens?',
    a: 'Nothing to fix. Take him out of the tee time and his group simply plays one player smaller, with a smaller pot. He isn’t charged, and nobody else’s game changes. A group left with one player plays for nothing that day, so move him into another group on the Pairings page if you can.',
  },
  {
    q: 'Is there a team Stableford?',
    a: 'Not any more. Teams of different sizes (a 3 against a 4) can’t be made fair on points, so TTO plays Stableford as individuals, within each group or across the whole field. For a team game, look at Best Ball.',
  },
  {
    q: 'Can we pay the front nine, back nine and overall?',
    a: 'Yes, with a pot on an 18-hole round. Under What it pays pick the parts; the front, the back and the full round are each their own Stableford with a share of the buy-in (split evenly unless you set the amounts). Per point pays the whole round only.',
  },
  {
    q: 'Can we play Stableford on a nine-hole round?',
    a: 'Yes. On a round set up as a nine, the points and the money cover those nine holes.',
  },
  {
    q: 'Can a player with no handicap play?',
    a: 'Not on a net Stableford: he can’t opt himself in without a Handicap Index, and TTO warns you if you put him in. Gross Stableford needs no handicap.',
  },
  {
    q: 'Is Modified Stableford available?',
    a: 'Not yet. TTO plays standard Stableford points. Modified Stableford (minus points for bogeys, more for birdies and eagles) is rare outside professional events; tell us if your group plays it.',
  },
];

export default function StablefordGame() {
  return (
    <article>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 38,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 8px',
          lineHeight: 1.15,
        }}
      >
        How to Set Up Stableford
      </h1>
      <style>{PRINT_CSS}</style>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 28px' }}
      >
        <p style={{ fontSize: 15, color: colors.gray400, margin: 0 }}>
          {guide.readMinutes} min read &middot; Game guide
        </p>
        <button
          type="button"
          className="guide-no-print"
          onClick={() => window.print()}
          style={{
            background: 'white',
            color: colors.green800,
            border: `1px solid ${colors.gray300}`,
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          🖨️ Print this guide
        </button>
      </div>

      <P>
        In Stableford you score <strong>points on every hole</strong> instead of counting strokes,
        and the <strong>most points wins</strong>. A bad hole costs you nothing more than that hole:
        once it&rsquo;s worth 0 you pick up and move on.
      </P>
      <div style={{ overflowX: 'auto', margin: '0 0 20px' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 15, minWidth: 320 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '6px 14px 6px 0', borderBottom: `2px solid ${colors.gray300}` }}>Your score on the hole</th>
              <th style={{ textAlign: 'right', padding: '6px 0', borderBottom: `2px solid ${colors.gray300}` }}>Points</th>
            </tr>
          </thead>
          <tbody>
            {[['Albatross or better', 5], ['Eagle', 4], ['Birdie', 3], ['Par', 2], ['Bogey', 1], ['Double bogey or worse', 0]].map(([k, v]) => (
              <tr key={k}>
                <td style={{ padding: '5px 14px 5px 0', borderBottom: `1px solid ${colors.gray200}` }}>{k}</td>
                <td style={{ padding: '5px 0', borderBottom: `1px solid ${colors.gray200}`, textAlign: 'right', fontWeight: 700 }}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <P>
        On a net game the score is your <strong>net</strong> score: a 5 on a par 4 where you get a
        stroke is a net par, 2 points. This guide sets up Stableford for your weekly round.
        Everyone plays for himself, either <strong>within his own group</strong> or against{' '}
        <strong>the whole field</strong>.
      </P>

      <StepTracker
        storageKey="tto_guide_stableford_steps"
        steps={['Add Stableford', 'Who’s in', 'Play', 'Results & settle']}
        note="Steps 1 and 2 happen before the round; 3 and 4 on the day. Tap a step to jump to it."
      />

      <H2>Before you start</H2>
      <UL>
        <LI>
          <strong>Stableford needs live scoring.</strong> Every player in it needs his scores entered
          in the app as he plays, on his own phone or by the player keeping his group&rsquo;s card.
        </LI>
        <LI>
          <strong>Stableford isn&rsquo;t in your games list?</strong> Games come with the Plus plan and
          above. On Free or Starter, upgrade under Settings &rarr; Subscription &amp; Billing.
        </LI>
        <LI>
          Decide with your group: <strong>within each group or the whole field</strong>,{' '}
          <strong>net or gross</strong>, and how the money goes: <strong>a pot</strong> paid by
          places (for the whole round, or front, back and overall),{' '}
          <strong>so much a point against the average</strong>, or{' '}
          <strong>so much a point, player by player</strong>.
        </LI>
        <LI>
          <strong>Playing within each group?</strong> Set the groups on the{' '}
          <strong>Pairings</strong> page and save them. Groups can be different sizes: a foursome
          and a threesome is fine. With no pairings saved, everyone in the game is one group.
        </LI>
      </UL>

      <H2>Setup, step by step</H2>

      <Step n={1} title="Add Stableford" when="Before the round">
        <Heads>
          <strong>On a net game, every player needs a Handicap Index</strong> (and a preferred tee,
          so his course handicap can be worked out). A player without one can&rsquo;t opt himself in,
          and TTO warns you if you put him in.
        </Heads>
        <UL>
          <LI>
            Click <Btn>🎮 Games</Btn> on the tee time card and tick <strong>Stableford</strong>.
          </LI>
          <LI>
            <strong>Who plays whom:</strong> <Btn>Within each group</Btn> (the default) &mdash; each
            group on the Pairings page plays its own Stableford, with its own places and its own
            money; or <Btn>Whole field</Btn> &mdash; everyone in the game plays one Stableford.
          </LI>
          <LI>
            <strong>Scoring:</strong> <Btn>Net</Btn> or <Btn>Gross</Btn>. On net, the{' '}
            <strong>Allowance</strong> box sets the share of each player&rsquo;s course handicap he
            plays off: 95% by default, the USGA&rsquo;s figure for individual Stableford.
          </LI>
          <LI>
            <strong>Money:</strong>
            <UL>
              <LI>
                <Btn>Pot</Btn> (the default): the wager is each player&rsquo;s{' '}
                <strong>buy-in</strong>. Then pick the places: <Btn>Winner takes all</Btn>,{' '}
                <Btn>Top 2 — 60 / 40</Btn> or <Btn>Top 3 — 50 / 30 / 20</Btn>. Within each group,
                every group has its own pot from its own players&rsquo; buy-ins.
              </LI>
              <LI>
                <Btn>Per point vs the group</Btn> (<Btn>Per point vs the field</Btn> on the whole
                field): the wager is so much a point. Each player wins or pays that amount for every
                point he is above or below the average.
              </LI>
              <LI>
                <Btn>Per point, player by player</Btn>: every pair of players (within each group,
                every pair in the group) settles the difference in points at so much a point.
              </LI>
            </UL>
            A $0 wager makes it results only: TTO keeps the points and the places and settles no
            money.
          </LI>
          <LI>
            <strong>What it pays</strong> (a pot on an 18-hole round): the whole round, or the nines
            &mdash; for example <Btn>Front, back &amp; overall</Btn>. The front nine, the back nine and
            the full round are each their own Stableford with their own places and a share of the
            buy-in, split evenly unless you type the amounts (they must add up to the buy-in). Each
            player keeps his 18-hole strokes where they fall.
          </LI>
          <LI>
            Click <Btn>Save Games</Btn>.
          </LI>
        </UL>
      </Step>

      <Step n={2} title="Choose who's in" when="After you save, until the first score">
        <UL>
          <LI>
            After you save, the game shows a <strong>Player Opt-Ins</strong> list. Click a
            player&rsquo;s <strong>— Out</strong> to make it <strong>✓ In</strong>, or the reverse.
          </LI>
          <LI>
            Players can also tap <Btn>Opt In</Btn> in their own app until the first score of the
            round is entered. A player whose Handicap Index hasn&rsquo;t been updated in a while is let
            in, with a reminder to update it.
          </LI>
        </UL>
      </Step>

      <H2>On the day</H2>

      <Step n={3} title="Play" when="During the round">
        <UL>
          <LI>
            Each player enters his scores hole by hole, or one player keeps the card for his group.
          </LI>
          <LI>
            <strong>PTS on the hole screen:</strong> the dark bar at the top of the scoring screen
            shows each player&rsquo;s points so far, updated every time he moves to the next hole.
          </LI>
          <LI>
            <strong>Nothing left on the hole?</strong> Tap <strong>X</strong> to pick up. A picked-up
            hole scores 0 points.
          </LI>
          <LI>
            The standings are in the app: <strong>🏆 Leaderboard</strong> &rarr;{' '}
            <strong>💰 Games</strong>, with each player&rsquo;s points, place and holes played. Within
            each group, every group has its own heading, e.g.{' '}
            <em>&ldquo;Group 1 · 8:30&rdquo;</em>, with its players and their places in that group
            beneath. With front, back and overall, tabs switch between the parts.
          </LI>
        </UL>
      </Step>

      <Step n={4} title="Results and settling up" when="When the last card is in">
        <UL>
          <LI>
            The round finalizes itself once every card is complete, or click{' '}
            <Btn>🏁 Finalize Round &amp; Save Settlements</Btn> in <Btn>🎮 Games</Btn>.
          </LI>
          <LI>
            <strong>Pot:</strong> the places are paid from the pot. Players tied on points share the
            money for the places they cover; the order shown is decided on the cards (the points over
            the last 9, 6, 3 and 1 holes). A pot pays only once every card is complete.
          </LI>
          <LI>
            <strong>Per point:</strong> the amounts run live as the round goes and are final with
            the last card.
          </LI>
          <LI>
            Every player gets a statement showing whom he pays or collects from, with both sides&rsquo;
            points on each line, for example <em>&ldquo;Stableford pot · 31 pts vs 27&rdquo;</em>.
            The statement&rsquo;s <strong>Stableford</strong> section lists the places and points.
          </LI>
          <LI>
            Within each group, the statement lists each group under its own heading, and a player
            only ever pays or collects from players in his own group.
          </LI>
        </UL>
        <H3>Examples</H3>
        <UL>
          <LI>
            <strong>Pot, $5 buy-in, four players, winner takes all:</strong> Al 36, Bob 33, Cal 31,
            Don 29. Al collects $5 from each of the others, $15 in all.
          </LI>
          <LI>
            <strong>Per point vs the field, $1 a point:</strong> same scores, field average 32.25.
            Al +$3.75, Bob +$0.75, Cal &minus;$1.25, Don &minus;$3.25.
          </LI>
          <LI>
            <strong>Per point, player by player, $1 a point:</strong> Al collects $3 from Bob, $5
            from Cal and $7 from Don.
          </LI>
          <LI>
            <strong>Within each group, $5 pot, winner takes all, a foursome and a threesome:</strong>{' '}
            the foursome&rsquo;s pot is $20 and its winner collects $15 net; the threesome&rsquo;s pot
            is $15 and its winner collects $10 net.
          </LI>
        </UL>
      </Step>

      <H2>Running Stableford in an event?</H2>
      <P>
        Set the event up with its format guide (📖 Guides &rarr; Setting up Events / Tournaments) and
        tick <strong>Stableford</strong> at the step where you add the games. Players follow their
        points in the app.
      </P>

      <FAQ id="stableford-game" faqs={FAQS} />
      <div className="guide-no-print">
        <GuideCTA />
      </div>
    </article>
  );
}
